import { useState, useEffect } from 'react';
import { useSocket } from '../hooks/useSocket';
import { useBranch } from '../contexts/BranchContext';

const statusColors = {
  draft: '#95a5a6',
  submitted: '#3498db',
  preparing: '#f39c12',
  served: '#27ae60',
  cancelled: '#e74c3c'
};

export default function OrderManagement({ apiUrl }) {
  const { effectiveBranchId, isAllBranches } = useBranch();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  // จัดการออเดอร์ต้องระบุสาขาเดียวเสมอ ห้ามเผลอทำตอนเลือก "รวมทุกสาขา" เพราะ
  // effectiveBranchId จะ fallback ไปสาขาแรกตามลำดับตัวอักษรแทน ไม่ใช่สาขาที่ตั้งใจ
  const blocked = isAllBranches || !effectiveBranchId;

  // WebSocket: Listen for real-time order updates
  useSocket(apiUrl, {
    'order:created': (data) => {
      console.log('[OrderManagement] New order:', data);
      loadOrders(); // Reload all orders
    },
    'order:updated': (data) => {
      console.log('[OrderManagement] Order updated:', data);
      // Update specific order in state
      setOrders(prev => prev.map(order =>
        (order._id || order.id) === data.orderId ? { ...order, ...data.updates } : order
      ));
    },
    'order:deleted': (data) => {
      console.log('[OrderManagement] Order deleted:', data);
      // Remove order from state
      setOrders(prev => prev.filter(order => (order._id || order.id) !== data.orderId));
    }
  }, { token: localStorage.getItem('adminToken'), branchId: effectiveBranchId });

  useEffect(() => {
    if (blocked) {
      setLoading(false);
      return;
    }
    loadOrders();
    // No more polling interval - using WebSocket events instead
  }, [blocked]);

  const loadOrders = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${apiUrl}/orders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();

      // Ensure data is an array before sorting
      if (Array.isArray(data)) {
        setOrders(data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
      } else if (data.data && Array.isArray(data.data)) {
        setOrders(data.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
      } else {
        console.warn('Unexpected data format:', data);
        setOrders([]);
      }
    } catch (err) {
      console.error('Failed to load orders:', err);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${apiUrl}/orders/${orderId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        loadOrders();
      }
    } catch (err) {
      console.error('Failed to update order:', err);
    }
  };

  if (blocked) {
    return (
      <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
        <div style={{ padding: '1.25rem', background: '#fff3cd', color: '#856404', borderRadius: '10px', fontWeight: 600 }}>
          ⚠️ กรุณาเลือกสาขาที่แน่นอนจากเมนูด้านบนก่อน — จัดการออเดอร์ต้องระบุสาขาเดียวเสมอ
          จะเลือก "รวมทุกสาขา" ไม่ได้เพื่อป้องกันการทำรายการผิดสาขาโดยไม่ตั้งใจ
        </div>
      </div>
    );
  }

  if (loading) {
    return <div>กำลังโหลดรายการสั่งอาหาร...</div>;
  }

  return (
    <div>
      <div className="page-header">
        <h2>จัดการรายการสั่งอาหาร</h2>
        <p>จัดการและติดตามออเดอร์ทั้งหมด</p>
      </div>

      <div className="card">
        <h3 style={{ marginBottom: '1rem' }}>ออเดอร์ทั้งหมด ({orders.length})</h3>
        <div style={{ overflowX: 'auto' }}>
        <table className="table-nowrap">
          <thead>
            <tr>
              <th>รหัสออเดอร์</th>
              <th>โต๊ะ</th>
              <th>รายการ</th>
              <th>สถานะ</th>
              <th>เวลาสั่ง</th>
              <th>จัดการ</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order) => (
              <tr key={order._id || order.id}>
                <td>{(order._id || order.id || '').toString().slice(-6)}</td>
                <td>{order.tableCode || '-'}</td>
                <td>{order.items?.length || 0} รายการ</td>
                <td>
                  <span className={`status-badge ${order.status}`}>
                    {order.status === 'draft' ? 'แบบร่าง' :
                     order.status === 'submitted' ? 'ส่งแล้ว' :
                     order.status === 'preparing' ? 'กำลังเตรียม' :
                     order.status === 'served' ? 'เสิร์ฟแล้ว' :
                     order.status === 'cancelled' ? 'ยกเลิก' : order.status}
                  </span>
                </td>
                <td>{new Date(order.createdAt).toLocaleString('th-TH')}</td>
                <td>
                  <select
                    value={order.status}
                    onChange={(e) => updateOrderStatus(order._id || order.id, e.target.value)}
                    style={{ padding: '0.5rem', borderRadius: '4px' }}
                  >
                    <option value="draft">แบบร่าง</option>
                    <option value="submitted">ส่งแล้ว</option>
                    <option value="preparing">กำลังเตรียม</option>
                    <option value="served">เสิร์ฟแล้ว</option>
                    <option value="cancelled">ยกเลิก</option>
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
